import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  FileText, Check, X, Eye, RefreshCw, Car, Shield, CreditCard,
  Clock, Mail, Phone, MapPin, AlertTriangle, CheckCircle, XCircle,
  Calendar, User, ExternalLink, Download, ChevronDown, ChevronUp,
  Filter, Search, Bell, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

export default function DriverDocumentVerification() {
  const { api } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [verifyingDoc, setVerifyingDoc] = useState(null);
  const [expiringDrivers, setExpiringDrivers] = useState([]);
  const [showExpiringModal, setShowExpiringModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [showDocRejectModal, setShowDocRejectModal] = useState(false);
  const [docToReject, setDocToReject] = useState(null);
  const [docRejectionReason, setDocRejectionReason] = useState('');

  useEffect(() => {
    fetchApplications();
    fetchExpiringDocuments();
  }, [statusFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('app_type', 'driver');
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/admin/applications?${params.toString()}`);
      setApplications(response.data.filter(app => app.type === 'driver'));
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiringDocuments = async () => {
    try {
      const response = await api.get('/admin/drivers/expiring-documents?days=30');
      setExpiringDrivers(response.data.drivers || []);
    } catch (error) {
      console.error('Failed to fetch expiring documents');
    }
  };

  const handleVerifyDocument = async (appId, docType, verified) => {
    setVerifyingDoc(docType);
    try {
      await api.post(`/admin/applications/${appId}/verify-document`, {
        document_id: docType,
        verified: verified,
        admin_notes: verified ? 'Document verified' : 'Document rejected'
      });
      toast.success(`Document ${verified ? 'verified' : 'rejected'}`);
      
      // Refresh application data
      const response = await api.get(`/applications/driver/${appId}/documents`);
      setApplications(prev => prev.map(app => 
        app.id === appId ? { ...app, documents: response.data.documents } : app
      ));
    } catch (error) {
      toast.error('Failed to update document verification');
    } finally {
      setVerifyingDoc(null);
    }
  };

  const handleApprove = async (appId) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/admin/applications/${appId}/approve`);
      toast.success('Application approved! Login credentials sent via email.');
      if (response.data.driver_login_code) {
        toast.info(`Driver Code: ${response.data.driver_login_code}`, { duration: 10000 });
      }
      fetchApplications();
      setSelectedApp(null);
      setExpandedApp(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.put(`/admin/applications/${selectedApp.id}/reject`, {
        reason: rejectionReason
      });
      toast.success('Application rejected');
      fetchApplications();
      setShowRejectModal(false);
      setSelectedApp(null);
      setExpandedApp(null);
      setRejectionReason('');
    } catch (error) {
      toast.error('Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestDocuments = async (appId, driverEmail) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/applications/${appId}/request-documents`);
      toast.success(`Document re-upload request sent to ${driverEmail}`);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send document request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (!applicationToDelete) return;
    
    setActionLoading(true);
    try {
      await api.delete(`/admin/applications/${applicationToDelete.id}`);
      toast.success(`Application for ${applicationToDelete.email} deleted successfully`);
      fetchApplications();
      setShowDeleteModal(false);
      setApplicationToDelete(null);
      setExpandedApp(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectDocument = async () => {
    if (!docToReject || !docRejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post(`/admin/applications/${docToReject.appId}/reject-document`, {
        document_type: docToReject.docType,
        rejection_reason: docRejectionReason
      });
      toast.success(`Document rejected. Re-upload request sent to driver.`);
      fetchApplications();
      setShowDocRejectModal(false);
      setDocToReject(null);
      setDocRejectionReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject document');
    } finally {
      setActionLoading(false);
    }
  };

  const openDocRejectModal = (appId, docType, appEmail) => {
    setDocToReject({ appId, docType, appEmail });
    setShowDocRejectModal(true);
  };

  const getDocumentViewUrl = (app, docUrl) => {
    // If it's an API path, we need to fetch with auth
    if (docUrl && docUrl.startsWith('/api/documents/')) {
      // Convert to full URL with token for viewing
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      return `${baseUrl}${docUrl}`;
    }
    return docUrl;
  };

  const runExpirationCheck = async () => {
    try {
      const response = await api.post('/admin/drivers/check-expirations');
      toast.success(`Expiration check complete: ${response.data.drivers_suspended} suspended, ${response.data.drivers_warned} warned`);
      fetchExpiringDocuments();
    } catch (error) {
      toast.error('Failed to run expiration check');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_assessment: 'bg-yellow-100 text-yellow-800',
      pending_documents: 'bg-blue-100 text-blue-800',
      pending_review: 'bg-orange-100 text-orange-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending_assessment: 'Pending Assessment',
      pending_documents: 'Awaiting Documents',
      pending_review: 'Ready for Review',
      approved: 'Approved',
      rejected: 'Rejected'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getDocIcon = (type) => {
    switch(type) {
      case 'license': return <CreditCard className="w-5 h-5" />;
      case 'insurance': return <Shield className="w-5 h-5" />;
      case 'registration': return <Car className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const filteredApplications = applications.filter(app => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return app.full_name?.toLowerCase().includes(search) ||
           app.email?.toLowerCase().includes(search) ||
           app.city?.toLowerCase().includes(search);
  });

  const isDocExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiry <= thirtyDaysFromNow;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Driver Document Verification</h1>
          <p className="text-slate-500">Review and verify driver documents for approval</p>
        </div>
        <div className="flex gap-3">
          {expiringDrivers.length > 0 && (
            <Button 
              variant="outline" 
              className="text-amber-600 border-amber-300"
              onClick={() => setShowExpiringModal(true)}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {expiringDrivers.length} Expiring
            </Button>
          )}
          <Button variant="outline" onClick={runExpirationCheck}>
            <Bell className="w-4 h-4 mr-2" />
            Check Expirations
          </Button>
          <Button variant="outline" onClick={fetchApplications}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Status:</span>
          </div>
          <div className="flex gap-2">
            {['all', 'pending_documents', 'pending_review', 'approved', 'rejected'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {status === 'all' ? 'All' : 
                 status === 'pending_documents' ? 'Awaiting Docs' :
                 status === 'pending_review' ? 'Ready for Review' :
                 status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex-1 max-w-xs ml-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {applications.filter(a => a.status === 'pending_review').length}
              </p>
              <p className="text-xs text-slate-500">Pending Review</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {applications.filter(a => a.status === 'pending_documents').length}
              </p>
              <p className="text-xs text-slate-500">Awaiting Docs</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {applications.filter(a => a.status === 'approved').length}
              </p>
              <p className="text-xs text-slate-500">Approved</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{expiringDrivers.length}</p>
              <p className="text-xs text-slate-500">Expiring Soon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Applications List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 text-orange-500 animate-spin" />
            <p className="text-slate-500">Loading applications...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No applications found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredApplications.map(app => (
              <div key={app.id} className="p-4">
                {/* Application Header */}
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-slate-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{app.full_name}</h3>
                        {getStatusBadge(app.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {app.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {app.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <Car className="w-3 h-3" /> {app.vehicle_type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {app.status === 'pending_review' && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={(e) => { e.stopPropagation(); handleApprove(app.id); }}
                          disabled={actionLoading}
                          data-testid={`approve-btn-${app.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setSelectedApp(app); 
                            setShowRejectModal(true); 
                          }}
                          disabled={actionLoading}
                          data-testid={`reject-btn-${app.id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {(app.status === 'rejected' || app.status === 'pending_documents') && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleRequestDocuments(app.id, app.email); 
                        }}
                        disabled={actionLoading}
                        data-testid={`request-docs-btn-${app.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Request Documents
                      </Button>
                    )}
                    {/* Delete button - always visible for any status */}
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setApplicationToDelete(app);
                        setShowDeleteModal(true);
                      }}
                      disabled={actionLoading}
                      data-testid={`delete-btn-${app.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                    {expandedApp === app.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Document Details */}
                {expandedApp === app.id && app.documents && Object.keys(app.documents).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Uploaded Documents</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(app.documents).map(([docType, docData]) => (
                        <div 
                          key={docType}
                          className={`border rounded-lg p-4 ${
                            docData.verified 
                              ? 'border-green-200 bg-green-50' 
                              : 'border-slate-200 bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                docData.verified ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {getDocIcon(docType)}
                              </div>
                              <span className="font-medium text-slate-800 capitalize">
                                {docType.replace('_', ' ')}
                              </span>
                            </div>
                            {docData.verified ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : app.status === 'pending_review' && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleVerifyDocument(app.id, docType, true)}
                                  disabled={verifyingDoc === docType}
                                  className="p-1 rounded bg-green-100 text-green-600 hover:bg-green-200"
                                  title="Verify"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openDocRejectModal(app.id, docType, app.email)}
                                  disabled={verifyingDoc === docType}
                                  className="p-1 rounded bg-red-100 text-red-600 hover:bg-red-200"
                                  title="Reject & Request Re-upload"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Document Preview/Link */}
                          {docData.document_url && (
                            <button
                              onClick={async () => {
                                // For API-served documents, need to fetch with auth
                                // Check for both /api/documents/ (legacy) and /documents/ (correct) prefixes
                                const isApiDocument = docData.document_url.startsWith('/api/documents/') || 
                                                     docData.document_url.startsWith('/documents/');
                                if (isApiDocument) {
                                  try {
                                    // Remove /api prefix if present since axios baseURL already includes /api
                                    const docPath = docData.document_url.startsWith('/api/') 
                                      ? docData.document_url.substring(4)  // Remove '/api' prefix
                                      : docData.document_url;
                                    const response = await api.get(docPath, { responseType: 'blob' });
                                    // Get the content type from response headers for proper blob creation
                                    const contentType = response.headers['content-type'] || 'application/octet-stream';
                                    const blob = new Blob([response.data], { type: contentType });
                                    const url = window.URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                  } catch (err) {
                                    console.error('Document fetch error:', err);
                                    toast.error('Failed to load document');
                                  }
                                } else {
                                  window.open(docData.document_url, '_blank');
                                }
                              }}
                              className="block w-full mb-3 rounded-lg overflow-hidden border border-slate-200 bg-white hover:shadow-md transition-shadow text-left"
                            >
                              <div className="aspect-video bg-slate-100 flex items-center justify-center">
                                <div className="text-center p-4">
                                  <FileText className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                                  <span className="text-xs text-blue-600 flex items-center gap-1 justify-center">
                                    <Eye className="w-3 h-3" /> View Document
                                  </span>
                                </div>
                              </div>
                            </button>
                          )}
                          
                          {/* Rejection info if document was rejected */}
                          {docData.rejected && (
                            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
                              <span className="font-medium text-red-700">Rejected: </span>
                              <span className="text-red-600">{docData.rejection_reason || 'See email for details'}</span>
                            </div>
                          )}
                          
                          {/* Document Details */}
                          <div className="space-y-1 text-sm">
                            {docData.document_number && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">
                                  {docType === 'license' ? 'License #:' : 
                                   docType === 'insurance' ? 'Policy #:' : 
                                   docType === 'registration' ? 'Reg #:' : 'Number:'}
                                </span>
                                <span className="font-medium text-slate-700">{docData.document_number}</span>
                              </div>
                            )}
                            {docData.policy_number && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Policy #:</span>
                                <span className="font-medium text-slate-700">{docData.policy_number}</span>
                              </div>
                            )}
                            {docData.registration_number && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Reg #:</span>
                                <span className="font-medium text-slate-700">{docData.registration_number}</span>
                              </div>
                            )}
                            {docData.expiry_date && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Expires:</span>
                                <span className={`font-medium ${
                                  isDocExpiringSoon(docData.expiry_date) 
                                    ? 'text-amber-600' 
                                    : 'text-slate-700'
                                }`}>
                                  {new Date(docData.expiry_date).toLocaleDateString()}
                                  {isDocExpiringSoon(docData.expiry_date) && (
                                    <AlertTriangle className="w-3 h-3 inline ml-1" />
                                  )}
                                </span>
                              </div>
                            )}
                            {docData.uploaded_at && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Uploaded:</span>
                                <span className="text-slate-700">
                                  {new Date(docData.uploaded_at).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                            {/* Additional Document Metadata for Audit Trail */}
                            {docData.file_size && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">File Size:</span>
                                <span className="text-slate-700">
                                  {docData.file_size > 1024 * 1024 
                                    ? `${(docData.file_size / (1024 * 1024)).toFixed(2)} MB`
                                    : `${(docData.file_size / 1024).toFixed(1)} KB`}
                                </span>
                              </div>
                            )}
                            {docData.content_type && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Format:</span>
                                <span className="text-slate-700 uppercase text-xs">
                                  {docData.content_type.split('/')[1] || docData.content_type}
                                </span>
                              </div>
                            )}
                            {docData.original_filename && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Original File:</span>
                                <span className="text-slate-700 text-xs truncate max-w-[150px]" title={docData.original_filename}>
                                  {docData.original_filename}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Assessment Score */}
                    {app.assessment_score !== null && (
                      <div className="mt-4 flex items-center gap-2 text-sm">
                        <span className="text-slate-500">Assessment Score:</span>
                        <span className={`font-semibold ${
                          app.assessment_passed ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {app.assessment_score}/5 {app.assessment_passed ? '✓ Passed' : '✗ Failed'}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* No Documents Message */}
                {expandedApp === app.id && (!app.documents || Object.keys(app.documents).length === 0) && (
                  <div className="mt-4 pt-4 border-t border-slate-100 text-center py-6">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-slate-500 text-sm">No documents uploaded yet</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to reject {selectedApp?.full_name}'s application?
            </p>
            <div>
              <Label htmlFor="rejection-reason">Rejection Reason</Label>
              <Input
                id="rejection-reason"
                placeholder="Enter reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={actionLoading || !rejectionReason.trim()}
            >
              {actionLoading ? 'Rejecting...' : 'Reject Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expiring Documents Modal */}
      <Dialog open={showExpiringModal} onOpenChange={setShowExpiringModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Drivers with Expiring Documents
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {expiringDrivers.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No expiring documents found</p>
            ) : (
              <div className="space-y-3">
                {expiringDrivers.map(driver => (
                  <div key={driver.driver_id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-900">{driver.name}</h4>
                        <p className="text-sm text-slate-500">{driver.email}</p>
                      </div>
                      {driver.is_suspended && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                          Suspended
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {driver.expiring_documents.map(doc => (
                        <div 
                          key={doc.type}
                          className={`flex items-center justify-between text-sm p-2 rounded ${
                            doc.status === 'expired' ? 'bg-red-50' : 'bg-amber-50'
                          }`}
                        >
                          <span className="capitalize">{doc.type.replace('_', ' ')}</span>
                          <span className={doc.status === 'expired' ? 'text-red-600 font-medium' : 'text-amber-600'}>
                            {doc.status === 'expired' ? 'EXPIRED' : `${doc.days_until_expiry} days left`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpiringModal(false)}>
              Close
            </Button>
            <Button onClick={runExpirationCheck}>
              <Bell className="w-4 h-4 mr-2" />
              Send Reminders & Suspend Expired
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to permanently delete this application?
            </p>
            {applicationToDelete && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Name:</span>
                  <span className="font-medium text-slate-900">{applicationToDelete.full_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Email:</span>
                  <span className="font-medium text-slate-900">{applicationToDelete.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Status:</span>
                  {getStatusBadge(applicationToDelete.status)}
                </div>
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                This action will:
              </p>
              <ul className="text-sm text-amber-700 mt-2 ml-5 list-disc">
                <li>Delete the application record</li>
                <li>Delete all uploaded documents</li>
                <li>Allow the user to submit a new application</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteModal(false);
              setApplicationToDelete(null);
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteApplication}
              disabled={actionLoading}
            >
              {actionLoading ? 'Deleting...' : 'Delete Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Rejection Modal */}
      <Dialog open={showDocRejectModal} onOpenChange={setShowDocRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="w-5 h-5" />
              Reject Document & Request Re-upload
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Rejecting this document will notify the driver to re-upload it. Please provide a clear reason.
            </p>
            {docToReject && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Document:</span>
                  <span className="font-medium text-slate-900 capitalize">{docToReject.docType.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-slate-500">Driver Email:</span>
                  <span className="font-medium text-slate-900">{docToReject.appEmail}</span>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="doc-rejection-reason">Rejection Reason *</Label>
              <textarea
                id="doc-rejection-reason"
                className="w-full mt-2 p-3 border rounded-lg text-sm"
                rows={3}
                placeholder="e.g., Document is blurry, expired date not visible, name doesn't match application..."
                value={docRejectionReason}
                onChange={(e) => setDocRejectionReason(e.target.value)}
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <Mail className="w-4 h-4 inline mr-1" />
                An email will be sent to the driver with your rejection reason and a link to upload a new document.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDocRejectModal(false);
              setDocToReject(null);
              setDocRejectionReason('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectDocument}
              disabled={actionLoading || !docRejectionReason.trim()}
            >
              {actionLoading ? 'Rejecting...' : 'Reject & Notify Driver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
